import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import logo from '../assets/img/Oman.png';

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, isRTL, language, changeLanguage } = useLanguage();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Determine if we're on the main dashboard or a sub-page
  const isMainDashboard = location.pathname === '/dashboard';
  const isProfileSetupPage = location.pathname === '/dashboard/profile-setup';
  const shouldBackToHome = isMainDashboard || isProfileSetupPage;
  const backButtonText = shouldBackToHome ? t('dashboard.backToHome') : t('dashboard.title');
  const backButtonPath = shouldBackToHome ? '/' : '/dashboard';

  return (
    <div
      className="min-h-screen bg-white text-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header - Always LTR */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-50 border-b border-slate-200 bg-white"
        dir="ltr"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          {/* Left: Back button (Home or Dashboard) */}
          <Link
            to={backButtonPath}
            className="inline-flex items-center gap-2 rounded-full border border-[#0AB0CE]/50 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#111034] shadow-sm hover:bg-[#0AB0CE]/5 transition-all"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#0AB0CE] to-[#111034] text-white">
              {/* Arrow icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className={`h-3 w-3 ${isRTL ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </span>
            <span className="hidden xs:inline">{backButtonText}</span>
            <span className="xs:hidden">{shouldBackToHome ? t('dashboard.home') : t('dashboard.title')}</span>
          </Link>

          {/* Center: Logo & title */}
          <div className="flex items-center gap-3">
            <img src={logo} alt="Logo" className="h-7 w-auto" />
          <div className="hidden sm:block">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                {t('dashboard.adminConsole') || 'Admin Console'}
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {t('dashboard.title')}
              </p>
            </div>
          </div>

          {/* Right: User + language + logout */}
          <div className="flex items-center gap-3">
            <div className={`hidden sm:flex flex-col leading-tight ${isRTL ? 'items-start' : 'items-end'}`}>
              <span className="text-xs font-medium text-slate-900">
                {user?.full_name || user?.email}
              </span>
              <span className="text-[10px] text-slate-500">
                {t('dashboard.welcome')}
              </span>
            </div>
            {/* Language Toggle Button */}
            <button
              onClick={() => changeLanguage(language === 'en' ? 'ar' : 'en')}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0AB0CE]/50 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#111034] shadow-sm hover:bg-[#0AB0CE]/5 transition-all"
              title={language === 'en' ? t('dashboard.switchToArabic') : t('dashboard.switchToEnglish')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{language === 'en' ? 'AR' : 'EN'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1 rounded-full bg-red-500/90 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-red-500 transition-colors"
            >
              <span
                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/10"
                aria-hidden="true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </span>
              <span>{t('dashboard.logout')}</span>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
